/**
 * An enquiry's chat, in a side sheet. The chat is a log the realtor keeps:
 * saving a message records it and sends nothing. An open enquiry can be
 * added to, converted or dismissed; a converted or dismissed one is
 * read-only, and a converted one links to its lead and contact thread.
 */

import {
	ExternalLink,
	MessageCircle,
	Phone,
	RefreshCw,
	UserCheck,
} from "lucide-react";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	useDismissEnquiry,
	useEnquiry,
	useLogEnquiryMessage,
} from "@/features/realtor/hooks/use-realtor-crm";
import {
	type CrmMessage,
	type Enquiry,
	type EnquiryChannel,
	type EnquiryDetail,
	type MessageChannel,
	type MessageDirection,
	telUrl,
	whatsappUrl,
} from "@/features/realtor/services/realtor-crm-api.service";
import { formatBudgetRange } from "@/features/realtor/utils/listings-format";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";
import {
	ChannelPill,
	ChoiceChips,
	FormField,
	SELECT_CLASS,
} from "./enquiry-shared";

const MESSAGE_CHANNELS: readonly MessageChannel[] = [
	"whatsapp",
	"call",
	"sms",
	"email",
	"in_person",
	"other",
];

// The composer's starting channel: the one matching how the enquiry came in,
// or WhatsApp for a referral or "other".
const DEFAULT_MESSAGE_CHANNEL: Record<EnquiryChannel, MessageChannel> = {
	whatsapp: "whatsapp",
	phone_call: "call",
	walk_in: "in_person",
	referral: "whatsapp",
	other: "whatsapp",
};

interface EnquiryChatSheetProps {
	/** The enquiry to show; the sheet is closed while this is null. */
	enquiryId: string | null;
	onClose: () => void;
	onConvert: (enquiry: Enquiry) => void;
}

export function EnquiryChatSheet({
	enquiryId,
	onClose,
	onConvert,
}: EnquiryChatSheetProps): ReactElement {
	const { t } = useTranslation();
	// Keep showing the last enquiry while the sheet slides shut, instead of
	// dropping to the loading state for the length of the animation.
	const [shownId, setShownId] = useState(enquiryId);
	if (enquiryId && enquiryId !== shownId) setShownId(enquiryId);
	const query = useEnquiry(enquiryId ?? shownId);

	return (
		<Sheet
			open={enquiryId != null}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<SheetContent
				side="right"
				className="flex w-full flex-col gap-0 overflow-y-auto border-dashboard-border bg-dashboard-bg p-0 sm:max-w-[560px]"
			>
				{query.isPending ? (
					<div className="p-6">
						<SheetTitle className="sr-only">
							{t("realtor.enquiries.sheet.loading")}
						</SheetTitle>
						<SkeletonRows label={t("realtor.enquiries.sheet.loading")} />
					</div>
				) : query.isError ? (
					<div className="space-y-3 p-6" role="alert">
						<SheetTitle className="text-base text-dashboard-text">
							{t("realtor.enquiries.sheet.loadFailed")}
						</SheetTitle>
						<p className="text-sm text-dashboard-muted">
							{query.error.message}
						</p>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => query.refetch()}
							disabled={query.isFetching}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.enquiries.retry")}
						</StudioButton>
					</div>
				) : (
					<EnquiryChat
						detail={query.data}
						onClose={onClose}
						onConvert={onConvert}
					/>
				)}
			</SheetContent>
		</Sheet>
	);
}

function EnquiryChat({
	detail,
	onClose,
	onConvert,
}: {
	detail: EnquiryDetail;
	onClose: () => void;
	onConvert: (enquiry: Enquiry) => void;
}): ReactElement {
	const { t, i18n } = useTranslation();
	const { enquiry, messages } = detail;
	const isOpen = enquiry.status === "open";
	const whatsapp = whatsappUrl(enquiry.phoneCountryCode, enquiry.phone);
	const tel = telUrl(enquiry.phoneCountryCode, enquiry.phone);

	return (
		<>
			<SheetHeader className="space-y-2 border-b border-dashboard-border p-5 pr-12 text-left">
				<div className="flex flex-wrap items-center gap-2">
					<SheetTitle className="font-display text-2xl font-medium italic text-dashboard-text">
						{enquiry.name}
					</SheetTitle>
					<ChannelPill channel={enquiry.channel} />
				</div>
				<SheetDescription className="text-xs text-dashboard-muted">
					{t("realtor.enquiries.sheet.loggedAgo", {
						time: formatRelativeTime(enquiry.createdAt, i18n.language),
					})}
				</SheetDescription>
				{(whatsapp || tel) && (
					<div className="flex flex-wrap gap-2 pt-1">
						{whatsapp && (
							/* ph-no-capture: the number rides in the href, and
							   maskTextSelector cannot reach an attribute. */
							<a
								href={whatsapp}
								target="_blank"
								rel="noopener noreferrer"
								className="ph-no-capture inline-flex h-8 items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-paper px-3 text-xs font-medium text-dashboard-text hover:border-dashboard-accent"
							>
								<MessageCircle className="h-3.5 w-3.5" />
								{t("realtor.enquiries.sheet.whatsapp")}
							</a>
						)}
						{tel && (
							<a
								href={tel}
								className="ph-no-capture inline-flex h-8 items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-paper px-3 text-xs font-medium text-dashboard-text hover:border-dashboard-accent"
							>
								<Phone className="h-3.5 w-3.5" />
								{t("realtor.enquiries.sheet.call")}
							</a>
						)}
					</div>
				)}
			</SheetHeader>

			<div className="flex-1 space-y-5 p-5">
				<PulledFields detail={detail} />

				<section aria-labelledby="enquiry-transcript-heading">
					<h3
						id="enquiry-transcript-heading"
						className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted"
					>
						{t("realtor.enquiries.sheet.transcriptTitle")}
					</h3>
					{messages.length === 0 ? (
						<p className="text-sm text-dashboard-muted">
							{t("realtor.enquiries.sheet.noMessages")}
						</p>
					) : (
						<ol className="space-y-2" data-testid="enquiry-transcript">
							{messages.map((message) => (
								<MessageBubble
									key={message.id}
									message={message}
									theirName={enquiry.name}
								/>
							))}
						</ol>
					)}
				</section>

				{enquiry.status === "converted" && <ConvertedNote enquiry={enquiry} />}
				{enquiry.status === "dismissed" && (
					<p className="rounded-lg border border-dashboard-border bg-dashboard-surface p-3 text-sm text-dashboard-muted">
						{t("realtor.enquiries.sheet.dismissedNote")}
					</p>
				)}
			</div>

			{isOpen && (
				<div className="space-y-4 border-t border-dashboard-border p-5">
					<Composer enquiry={enquiry} />
					<div className="flex flex-wrap justify-end gap-2">
						<DismissButton enquiry={enquiry} onDismissed={onClose} />
						<StudioButton size="sm" onClick={() => onConvert(enquiry)}>
							<UserCheck className="h-4 w-4" />
							{t("realtor.enquiries.convertCta")}
						</StudioButton>
					</div>
				</div>
			)}
		</>
	);
}

function PulledFields({ detail }: { detail: EnquiryDetail }): ReactElement {
	const { t } = useTranslation();
	const { enquiry, listing } = detail;
	const budget = formatBudgetRange(enquiry.budgetMin, enquiry.budgetMax);
	const fields: Array<{ key: string; label: string; value: string }> = [];
	if (listing) {
		fields.push({
			key: "listing",
			label: t("realtor.enquiries.sheet.fields.listing"),
			value: `${listing.headline} · ${listing.locality}`,
		});
	}
	if (budget) {
		fields.push({
			key: "budget",
			label: t("realtor.enquiries.sheet.fields.budget"),
			value: budget,
		});
	}
	if (enquiry.phone) {
		fields.push({
			key: "phone",
			label: t("realtor.enquiries.sheet.fields.phone"),
			value: `+${enquiry.phoneCountryCode} ${enquiry.phone}`,
		});
	}
	if (enquiry.intent) {
		fields.push({
			key: "intent",
			label: t("realtor.enquiries.sheet.fields.intent"),
			value: enquiry.intent,
		});
	}

	return (
		<section
			aria-labelledby="enquiry-pulled-heading"
			className="rounded-lg border border-dashboard-border bg-dashboard-paper p-3"
		>
			<h3
				id="enquiry-pulled-heading"
				className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted"
			>
				{t("realtor.enquiries.sheet.pulledTitle")}
			</h3>
			<p className="mt-0.5 text-[11px] text-dashboard-muted">
				{t("realtor.enquiries.sheet.pulledHint")}
			</p>
			{fields.length === 0 ? (
				<p className="mt-2 text-sm text-dashboard-muted">
					{t("realtor.enquiries.sheet.pulledNone")}
				</p>
			) : (
				<ul className="mt-2 flex flex-wrap gap-2">
					{fields.map((field) => (
						<li
							key={field.key}
							data-testid={`pulled-${field.key}`}
							className="max-w-full rounded-lg bg-dashboard-surface px-3 py-1 text-xs text-dashboard-text"
						>
							<span className="text-dashboard-muted">{field.label}: </span>
							<span className="break-words font-medium">{field.value}</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function MessageBubble({
	message,
	theirName,
}: {
	message: CrmMessage;
	theirName: string;
}): ReactElement {
	const { t, i18n } = useTranslation();
	const mine = message.direction === "outbound";
	return (
		<li className={cn("flex", mine ? "justify-end" : "justify-start")}>
			<div
				className={cn(
					"max-w-[85%] rounded-2xl px-3 py-2 text-sm",
					mine
						? "rounded-br-sm bg-dashboard-accent text-dashboard-on-fill"
						: "rounded-bl-sm border border-dashboard-border bg-dashboard-paper text-dashboard-text",
				)}
			>
				{/* data-sensitive: masked in session replay. Free text between
				    a realtor and a buyer; the buyer never saw the recording
				    toggle. See lib/posthog.ts. */}
				<p data-sensitive className="whitespace-pre-wrap break-words">
					{message.body}
				</p>
				<p
					className={cn(
						"mt-1 text-[10px]",
						mine ? "text-dashboard-on-fill/80" : "text-dashboard-muted",
					)}
				>
					{mine ? t("realtor.enquiries.sheet.you") : theirName} ·{" "}
					{t(`realtor.enquiries.messageChannels.${message.channel}`)} ·{" "}
					{formatRelativeTime(message.occurredAt, i18n.language)}
				</p>
			</div>
		</li>
	);
}

function Composer({ enquiry }: { enquiry: Enquiry }): ReactElement {
	const { t } = useTranslation();
	const [direction, setDirection] = useState<MessageDirection>("outbound");
	const [channel, setChannel] = useState<MessageChannel>(
		DEFAULT_MESSAGE_CHANNEL[enquiry.channel],
	);
	const [body, setBody] = useState("");
	const [emptyError, setEmptyError] = useState(false);
	const logMessage = useLogEnquiryMessage();

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		const text = body.trim();
		if (!text) {
			setEmptyError(true);
			return;
		}
		logMessage.mutate(
			{ enquiryId: enquiry.id, input: { direction, channel, body: text } },
			{ onSuccess: () => setBody("") },
		);
	};

	return (
		<form onSubmit={handleSubmit} noValidate className="space-y-3">
			<div>
				<h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					{t("realtor.enquiries.composer.title")}
				</h3>
				<p className="mt-0.5 text-[11px] text-dashboard-muted">
					{t("realtor.enquiries.composer.hint")}
				</p>
			</div>
			<div className="flex flex-wrap items-end gap-3">
				<ChoiceChips
					name="composer-direction"
					legend={t("realtor.enquiries.composer.directionLabel")}
					value={direction}
					onChange={setDirection}
					options={[
						{
							value: "outbound",
							label: t("realtor.enquiries.composer.sent"),
						},
						{
							value: "inbound",
							label: t("realtor.enquiries.composer.said"),
						},
					]}
				/>
				<div className="min-w-[9rem] flex-1">
					<FormField
						id="composer-channel"
						label={t("realtor.enquiries.composer.channelLabel")}
					>
						<select
							id="composer-channel"
							className={SELECT_CLASS}
							value={channel}
							onChange={(e) => setChannel(e.target.value as MessageChannel)}
						>
							{MESSAGE_CHANNELS.map((option) => (
								<option key={option} value={option}>
									{t(`realtor.enquiries.messageChannels.${option}`)}
								</option>
							))}
						</select>
					</FormField>
				</div>
			</div>
			<FormField
				id="composer-body"
				label={t("realtor.enquiries.composer.bodyLabel")}
				error={
					emptyError ? t("realtor.enquiries.composer.emptyError") : undefined
				}
			>
				<Textarea
					id="composer-body"
					value={body}
					onChange={(e) => {
						setBody(e.target.value);
						setEmptyError(false);
					}}
					placeholder={t("realtor.enquiries.composer.bodyPlaceholder")}
					rows={2}
					maxLength={5000}
				/>
			</FormField>
			{logMessage.isError && (
				<p className="text-sm text-dashboard-pill-risk" role="alert">
					{logMessage.error.message}
				</p>
			)}
			<div className="flex justify-end">
				<StudioButton
					type="submit"
					variant="secondary"
					size="sm"
					disabled={logMessage.isPending}
				>
					{logMessage.isPending
						? t("realtor.enquiries.saving")
						: t("realtor.enquiries.composer.save")}
				</StudioButton>
			</div>
		</form>
	);
}

function DismissButton({
	enquiry,
	onDismissed,
}: {
	enquiry: Enquiry;
	onDismissed: () => void;
}): ReactElement {
	const { t } = useTranslation();
	const [confirming, setConfirming] = useState(false);
	const dismiss = useDismissEnquiry();

	const handleConfirm = (): void => {
		dismiss.mutate(enquiry.id, {
			onSuccess: () => {
				setConfirming(false);
				toast.success(t("realtor.enquiries.dismiss.successToast"));
				onDismissed();
			},
			onError: (error) => toast.error(error.message),
		});
	};

	return (
		<>
			<StudioButton
				variant="quiet"
				size="sm"
				onClick={() => setConfirming(true)}
			>
				{t("realtor.enquiries.dismiss.cta")}
			</StudioButton>
			<AlertDialog open={confirming} onOpenChange={setConfirming}>
				<AlertDialogContent className="w-[calc(100%-2rem)] rounded-xl border-dashboard-border bg-dashboard-bg">
					<AlertDialogHeader>
						<AlertDialogTitle className="text-dashboard-text">
							{t("realtor.enquiries.dismiss.confirmTitle", {
								name: enquiry.name,
							})}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-dashboard-muted">
							{t("realtor.enquiries.dismiss.confirmBody")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("realtor.enquiries.dismiss.keep")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								// Stay open until the dismiss lands, so a failure is seen.
								event.preventDefault();
								handleConfirm();
							}}
							disabled={dismiss.isPending}
						>
							{t("realtor.enquiries.dismiss.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function ConvertedNote({ enquiry }: { enquiry: Enquiry }): ReactElement {
	const { t } = useTranslation();
	return (
		<div className="space-y-2 rounded-lg border border-dashboard-border bg-dashboard-surface p-3">
			<p className="text-sm text-dashboard-text">
				{enquiry.contactId
					? t("realtor.enquiries.sheet.convertedNote")
					: t("realtor.enquiries.sheet.convertedNoteNoContact")}
			</p>
			<div className="flex flex-wrap gap-2">
				{enquiry.convertedLeadId && (
					<Link
						to={`/realtor/leads/${enquiry.convertedLeadId}`}
						className="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-paper px-3 text-xs font-medium text-dashboard-text hover:border-dashboard-accent"
					>
						<ExternalLink className="h-3.5 w-3.5" />
						{t("realtor.enquiries.sheet.openLead")}
					</Link>
				)}
				{enquiry.contactId && (
					<Link
						to={`/realtor/messages?contact=${enquiry.contactId}`}
						className="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-paper px-3 text-xs font-medium text-dashboard-text hover:border-dashboard-accent"
					>
						<MessageCircle className="h-3.5 w-3.5" />
						{t("realtor.enquiries.sheet.openThread")}
					</Link>
				)}
			</div>
		</div>
	);
}
